// DuplicateThreadBanner — Polish #4.
//
// Surfaces above the thread when the customer has another OPEN conversation
// somewhere in the inbox. Kimi almost always wants these folded together —
// replying to both separately leads to conflicting answers and the customer
// thinking we're not paying attention.
//
// Behavior:
//   - Runs off the same useCustomerHistory data powering the related-threads
//     widget further down the rail. No extra round-trip.
//   - Filters to status IN ('open','awaiting','snoozed') — resolved/spam
//     don't need merging.
//   - Renders nothing when there are zero hits.
//   - One-click "Merge into this" per candidate, with a ConfirmDialog in
//     front of the mutation. Because merging moves messages around we do
//     NOT want this firing on a fat-fingered click.
//   - On success the current thread's query invalidates (new messages come
//     in) and the source is swallowed.

import { useState } from 'react';
import { useCustomerHistory } from '@/lib/hooks/useCustomerHistory';
import { useMergeConversation } from '@/lib/hooks/useMergeConversation';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

interface DuplicateThreadBannerProps {
  conversationId: string;
  customerEmail: string | null | undefined;
}

const ACTIVE_STATUSES = new Set(['open', 'awaiting', 'snoozed', 'new']);

function relative(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days < 1) return 'today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function DuplicateThreadBanner({
  conversationId,
  customerEmail,
}: DuplicateThreadBannerProps) {
  const { data, isLoading } = useCustomerHistory(customerEmail ?? null, conversationId);
  const merge = useMergeConversation();
  const [pendingSourceId, setPendingSourceId] = useState<string | null>(null);

  if (!customerEmail || isLoading) return null;

  const duplicates = (data?.conversations ?? []).filter((c) =>
    ACTIVE_STATUSES.has(c.status),
  );
  if (duplicates.length === 0) return null;

  const pending = duplicates.find((c) => c.id === pendingSourceId) ?? null;
  const labelFor = (c: (typeof duplicates)[number]) =>
    c.ai_summary ?? c.subject ?? '(no subject)';

  return (
    <>
      <div
        className="dup-thread-banner"
        role="status"
        aria-live="polite"
      >
        <div className="dup-thread-lead">
          <div className="dup-thread-dot" aria-hidden />
          <div className="dup-thread-copy">
            <div className="dup-thread-title">
              {duplicates.length === 1
                ? 'Another open conversation with this customer'
                : `${duplicates.length} other open conversations with this customer`}
            </div>
            <div className="dup-thread-sub">
              Merging moves every message into this thread. The other thread gets marked resolved.
            </div>
          </div>
        </div>
        <ul className="dup-thread-list">
          {duplicates.map((c) => (
            <li key={c.id} className="dup-thread-row">
              <div className="dup-thread-row-text">
                <span className="dup-thread-row-title">{labelFor(c)}</span>
                <span className="dup-thread-row-meta">
                  {c.brand === 'md' ? 'MD' : 'HK'}
                  <span aria-hidden> · </span>
                  {c.status}
                  <span aria-hidden> · </span>
                  {relative(c.last_message_at)}
                </span>
              </div>
              <button
                type="button"
                className="btn dup-thread-action"
                disabled={merge.isPending}
                onClick={() => setPendingSourceId(c.id)}
              >
                Merge into this
              </button>
            </li>
          ))}
        </ul>
      </div>
      <ConfirmDialog
        open={pending !== null}
        title="Merge the two threads?"
        body={
          pending
            ? `Every message from "${labelFor(pending)}" will move into this thread. The other thread will be marked resolved and hidden. This can't be undone automatically, but nothing is deleted.`
            : ''
        }
        confirmLabel="Merge"
        busy={merge.isPending}
        onCancel={() => setPendingSourceId(null)}
        onConfirm={() => {
          if (!pending) return;
          merge.mutate(
            { targetId: conversationId, sourceId: pending.id },
            {
              onSuccess: () => setPendingSourceId(null),
              // On error keep the dialog open so Kimi can see the
              // Working state clear; a followup error-toast hook can
              // be wired later if we want explicit failure feedback.
              onError: () => setPendingSourceId(null),
            },
          );
        }}
      />
    </>
  );
}
